import { ValueWithSelection } from '../Mask';

import { JAVASCRIPT_DECIMAL_SEPARATOR, NEGATIVE_SIGN } from './constants';
import { NumberMaskOptions } from './types';

export const getMaskedNumberRegex = ({
    allowNegativeNumber,
    groupSeparator,
    decimalSeparator,
    groupSize,
    precision,
}: Partial<NumberMaskOptions>) => {
    const maskedNumberRegex = getMaskedNumberRegexString({ groupSeparator, decimalSeparator, groupSize, precision });
    return allowNegativeNumber ? new RegExp(`^-?${maskedNumberRegex}`) : new RegExp(`^${maskedNumberRegex}`);
};

const getMaskedNumberRegexString = ({
    groupSeparator,
    decimalSeparator,
    groupSize,
    precision,
}: Partial<NumberMaskOptions>) =>
    `((([0-9]{0,3}((\\${groupSeparator}{1}[0-9]{${groupSize}})+)?)|0)(\\${decimalSeparator}[0-9]{0,${precision}})?)?$`;

export interface FormatNumberArgs {
    vs: ValueWithSelection;
    maxPrecision?: number;
    groupSize: number;
    groupSeparator: string;
    decimalSeparator: string;
}

// Accepts unformatted value
export const formatNumber = ({
    vs,
    groupSize,
    groupSeparator,
    decimalSeparator,
    maxPrecision = 20,
}: FormatNumberArgs): ValueWithSelection => {
    const { value } = vs;
    let { start, end } = vs.selection;

    const splittedValue = value.split(JAVASCRIPT_DECIMAL_SEPARATOR);
    const [, floatingPart] = splittedValue;
    let [integerPart] = splittedValue;

    let negativeSign: string | undefined;
    if (integerPart.startsWith(NEGATIVE_SIGN)) {
        [negativeSign, integerPart] = integerPart.split(NEGATIVE_SIGN);
    }

    if (integerPart && integerPart.length > groupSize) {
        const chunks: string[] = splitByGroupSize(integerPart, groupSize);

        let groupSeparatorIndex: number = negativeSign !== undefined ? 1 : 0;
        chunks.forEach((chunk, index) => {
            if (index !== chunks.length - 1) {
                groupSeparatorIndex += chunk.length;

                if (start > groupSeparatorIndex) {
                    start++;
                }
                if (end > groupSeparatorIndex) {
                    end++;
                }
            }
        });

        integerPart = chunks.join(groupSeparator);
    }

    let returnedValue =
        floatingPart && maxPrecision > 0 ? `${integerPart}${decimalSeparator}${floatingPart}` : integerPart;
    if (negativeSign !== undefined) {
        returnedValue = `${NEGATIVE_SIGN}${returnedValue}`;
    }
    return { value: returnedValue, selection: { start, end } };
};

const splitByGroupSize = (value: string, groupSize: number): string[] => {
    const groupChunksRegExp = new RegExp(`\\d{${groupSize}}`, 'g');
    let chunks: string[] = [];
    let stringToChunk = value;

    const firstGroupIndex = stringToChunk.length % groupSize;
    if (firstGroupIndex !== 0) {
        const firstGroup = stringToChunk.slice(0, firstGroupIndex);
        chunks.push(firstGroup);
        stringToChunk = stringToChunk.slice(firstGroupIndex);
    }
    chunks = [...chunks, ...(stringToChunk.match(groupChunksRegExp) as string[])];

    return chunks;
};
